/**
 * Per-run mapping fixes: when the chosen dataset has no value for a variable an
 * evaluator reads by default, this run can point it at another path. Sent as
 * `mapping` on that evaluator's choice; the evaluator's own version is not
 * changed — unless "Also save as the evaluator's default" is ticked, which
 * PATCHes it (a new version) before the experiment is created.
 *
 * Whether the new path really has values is the server's call when the run
 * starts (a 422 says what is wrong); here an evaluator counts as fixed once
 * every missing variable has a path.
 */

import { useState } from "react";
import { usePatchEvaluators } from "@/features/evaluators/hooks";
import type { Evaluator, MappingStatus } from "@/features/evaluators/types";
import type { EvaluatorChoice, ExperimentEvaluator } from "../types";

/** `{evaluator_id: {variable: path}}`, only the variables the person set. */
export type Overrides = Record<string, Record<string, string>>;

export const statusOf = (e: Evaluator): MappingStatus => e.status ?? (e.compatible ? "ready" : "missing");

export function isFixed(e: Evaluator, override: Record<string, string> | undefined): boolean {
  const missing = e.missing ?? [];
  return missing.length > 0 && missing.every((m) => !!override?.[m]?.trim());
}

export const canRun = (e: Evaluator, overrides: Overrides) =>
  statusOf(e) !== "missing" || isFixed(e, overrides[e.id]);

function cleaned(override: Record<string, string> | undefined): Record<string, string> | null {
  const entries = Object.entries(override ?? {})
    .map(([name, path]) => [name, path.trim()] as const)
    .filter(([, path]) => path);
  return entries.length ? Object.fromEntries(entries) : null;
}

/** `{evaluator_ids}` as before, or `{evaluators}` when any pick has an override. */
export function evaluatorsPayload(
  ids: string[],
  overrides: Overrides,
): { evaluator_ids: string[] } | { evaluators: EvaluatorChoice[] } {
  if (!ids.some((id) => cleaned(overrides[id]))) return { evaluator_ids: ids };
  return {
    evaluators: ids.map((id) => {
      const mapping = cleaned(overrides[id]);
      return mapping ? { evaluator_id: id, mapping } : { evaluator_id: id };
    }),
  };
}

export function useEvaluatorFixes(held: ExperimentEvaluator[] = []) {
  const [overrides, setOverrides] = useState<Overrides>(() =>
    Object.fromEntries(held.map((e) => [e.id, e.remapped ?? {}])),
  );
  const [asDefault, setAsDefault] = useState<Set<string>>(() => new Set());
  const patch = usePatchEvaluators();

  return {
    overrides,
    asDefault,
    setPath: (id: string, name: string, path: string) =>
      setOverrides((o) => ({ ...o, [id]: { ...o[id], [name]: path } })),
    toggleDefault: (id: string) =>
      setAsDefault((s) => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    busy: patch.isPending,
    /** Saves the ticked fixes as new versions, then the request's evaluators part
     *  pointing at those new versions (still with the mapping, so the run reads
     *  exactly what was on screen). */
    payload: async (ids: string[]) => {
      const ticked = ids.filter((id) => asDefault.has(id) && cleaned(overrides[id]));
      const saved = ticked.length
        ? await patch.mutateAsync(ticked.map((id) => ({ id, body: { mapping: cleaned(overrides[id])! } })))
        : [];
      const newId = new Map(ticked.map((id, i) => [id, saved[i]?.id ?? id]));
      const moved = Object.fromEntries(Object.entries(overrides).map(([id, m]) => [newId.get(id) ?? id, m]));
      const result = evaluatorsPayload(
        ids.map((id) => newId.get(id) ?? id),
        moved,
      );
      if (!held.length) return result;
      const choices =
        "evaluators" in result
          ? result.evaluators
          : result.evaluator_ids.map((evaluator_id): EvaluatorChoice => ({ evaluator_id }));
      return {
        evaluators: choices.map((choice) => {
          const saved = held.find((e) => e.id === choice.evaluator_id);
          if (!saved) return choice;
          const mapping = saved.mapping
            ? Object.fromEntries(Object.entries(saved.mapping).map(([k, v]) => [k, v ?? ""]))
            : undefined;
          return {
            ...choice,
            ...(saved.version !== undefined ? { version: saved.version } : {}),
            ...(mapping ? { mapping: { ...mapping, ...choice.mapping } } : {}),
          };
        }),
      };
    },
  };
}

export type EvaluatorFixes = ReturnType<typeof useEvaluatorFixes>;
